/* PDP Pack Buy Box — in-place swap (single ↔ sampler)
   Fetches the sampler PDP once on first interaction, extracts its Moonbundle
   widget, injects it into the current page, and toggles visibility via a
   data attribute on <html>. If anything fails, falls back to a plain
   navigation to the sampler URL (the original anchor href). */
(() => {
  const root = document.documentElement;
  const STATE_ATTR = "data-pack-state";
  const FETCH_TIMEOUT_MS = 8_000;
  const scriptSrc =
    document.currentScript?.src ||
    document.querySelector('script[src*="pack-buy-box-swap.js"]')?.src ||
    "";
  const samplerOverrideImageUrl = scriptSrc
    ? new URL("Brainfuel_Watermelon.webp", scriptSrc).href
    : "";

  const singleMoonbundleWrap = document.querySelector(
    '[id*="moon_bundles_upsell"]',
  );
  const triggers = document.querySelectorAll("[data-pack-buy-box-cta]");

  if (!singleMoonbundleWrap || !triggers.length) return;

  // Sampler URL: comes from any trigger marked as "sampler" (V1 sampler card / V2 CTA)
  const samplerTrigger = document.querySelector(
    '[data-pack-buy-box-cta="sampler"]',
  );
  const samplerUrl = samplerTrigger?.getAttribute("href");
  if (!samplerUrl || !samplerUrl.includes("/products/")) return;

  // Normalize to same-origin path so fetch never tries a cross-origin request.
  // (The metafield often stores absolute production URLs like https://shop.com/products/...)
  let samplerPath;
  let samplerVariantIdFromUrl = null;
  try {
    const u = new URL(samplerUrl, location.origin);
    samplerPath = u.pathname;
    samplerVariantIdFromUrl = u.searchParams.get("variant");
  } catch (_) {
    samplerPath = samplerUrl.split("?")[0];
    const m = samplerUrl.match(/[?&]variant=(\d+)/);
    if (m) samplerVariantIdFromUrl = m[1];
  }

  // ATC: selected slot options → URL ?variant= → first available
  function resolveSamplerVariantId() {
    const product = state.samplerProduct;
    const slot = state.injectedSlot;

    if (product?.variants?.length && slot) {
      const checked = Array.from(
        slot.querySelectorAll(".variant-input-wrap [data-variant-input]:checked"),
      );
      if (checked.length) {
        const selected = {};
        checked.forEach((input) => {
          const key = input.getAttribute("data-index");
          if (key) selected[key] = input.value;
        });
        if (!Object.keys(selected).length && checked[0]?.value) {
          selected.option1 = checked[0].value;
        }
        const match =
          product.variants.find((variant) =>
            Object.keys(selected).every(
              (key) => String(variant[key] ?? "") === String(selected[key] ?? ""),
            ),
          ) ||
          product.variants.find((variant) => {
            const values = Object.values(selected);
            const options = variant.options || [];
            return values.every(
              (val) =>
                options.includes(val) ||
                variant.option1 === val ||
                variant.title === val,
            );
          });
        if (match) return String(match.id);
      }
    }

    if (samplerVariantIdFromUrl) {
      if (!product?.variants?.length) return samplerVariantIdFromUrl;
      const fromUrl = product.variants.find(
        (v) => String(v.id) === String(samplerVariantIdFromUrl),
      );
      if (fromUrl) return String(fromUrl.id);
    }

    if (!product?.variants?.length) return null;
    const available =
      product.variants.find((v) => v.available) || product.variants[0];
    return available ? String(available.id) : null;
  }

  const state = {
    mode: "single",
    samplerMarkup: null,
    samplerProduct: null,
    samplerCardIconUrl: null,
    inflightFetch: null,
    injectedSlot: null,
  };

  const singleCard = document.querySelector(".pack-buy-box--v1__card--single");
  const samplerCard = document.querySelector(
    ".pack-buy-box--v1__card--sampler",
  );

  function setCardIcon(card, imageUrl, className) {
    const icon = card?.querySelector(".pack-buy-box--v1__icon");
    if (!icon || !imageUrl || icon.dataset.realIcon === imageUrl) return;
    const existingImg = icon.querySelector("img");
    if (
      !icon.dataset.realIcon &&
      existingImg &&
      !existingImg.classList.contains("pack-buy-box--v1__real-icon") &&
      !icon.querySelector("svg")
    )
      return;

    const img = document.createElement("img");
    img.src = imageUrl;
    img.alt = "";
    img.loading = "eager";
    img.decoding = "async";
    if (className) img.className = className;

    icon.replaceChildren(img);
    icon.dataset.realIcon = imageUrl;
  }

  function resolveVariantImageUrl(img) {
    const imageUrl = img ? img.currentSrc || img.src || "" : "";
    if (imageUrl.includes("Brain_Fuel_5_Pack") && samplerOverrideImageUrl)
      return samplerOverrideImageUrl;
    return imageUrl;
  }

  function getVariantInputImageUrl(scope) {
    const checked = scope.querySelector(
      ".variant-input input:checked + label img",
    );
    const first = scope.querySelector(".variant-input label img");
    const img = checked || first;
    return resolveVariantImageUrl(img);
  }

  function syncV1CardIcons() {
    if (!root.classList.contains("ab-pack-v1")) return;

    const singlePicker = document.querySelector(
      "[data-dynamic-variants-enabled]:not(#pack-buy-box-sampler-slot [data-dynamic-variants-enabled])",
    );
    setCardIcon(
      singleCard,
      getVariantInputImageUrl(singlePicker || document),
      "pack-buy-box--v1__real-icon",
    );

    const samplerSlot = state.injectedSlot;
    const samplerPickerImage = samplerSlot
      ? getVariantInputImageUrl(samplerSlot)
      : "";
    setCardIcon(
      samplerCard,
      samplerPickerImage || state.samplerCardIconUrl,
      "pack-buy-box--v1__real-icon pack-buy-box--v1__real-icon--sampler",
    );
  }

  function prefetchSamplerCardIcon() {
    if (
      !root.classList.contains("ab-pack-v1") ||
      state.samplerCardIconUrl ||
      state.inflightFetch
    )
      return;
    loadSamplerData()
      .then(syncV1CardIcons)
      .catch(() => {});
  }

  async function loadSamplerData() {
    if (state.samplerMarkup && state.samplerProduct) return;
    if (state.inflightFetch) return state.inflightFetch;

    state.inflightFetch = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      try {
        const [htmlR, jsonR] = await Promise.all([
          fetch(samplerPath, { signal: controller.signal }),
          fetch(samplerPath + ".js", { signal: controller.signal }),
        ]);
        if (!htmlR.ok)
          throw new Error(`fetch ${samplerPath} returned ${htmlR.status}`);
        const html = await htmlR.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const comp = doc.querySelector(
          "moonbundle-component[data-moonbundle-bundle-id]",
        );
        if (!comp) throw new Error("no moonbundle-component in sampler PDP");
        state.samplerMarkup = comp.outerHTML;
        // Also grab the sampler's variant picker block so the injected slot
        // shows the sampler's "Select Flavor" option (single fixed pack)
        // instead of the hidden single product's flavor picker.
        const samplerVariantPicker = doc.querySelector(
          "[data-dynamic-variants-enabled]",
        );
        state.samplerVariantPickerMarkup = samplerVariantPicker
          ? samplerVariantPicker.outerHTML
          : "";
        state.samplerCardIconUrl = samplerVariantPicker
          ? getVariantInputImageUrl(samplerVariantPicker)
          : null;
        // Pull the test/widget scripts that render INTO the Moonbundle DOM —
        // session-info, loyalty badges, trust test. Their Liquid values are
        // baked in at render time, so re-injecting them gives us sampler-
        // correct numbers and they'll mount onto the sampler slot's options.
        const TEST_SCRIPT_PATTERN =
          /SESSIONS_PER_TIN|ab-loyalty-badge|ab-version-[ab]\b|ab-pdp-trust/i;
        state.samplerTestScripts = Array.from(doc.querySelectorAll("script"))
          .filter(
            (s) => !s.src && TEST_SCRIPT_PATTERN.test(s.textContent || ""),
          )
          .map((s) => s.textContent || "");
        if (jsonR.ok) state.samplerProduct = await jsonR.json();
      } finally {
        clearTimeout(timer);
        state.inflightFetch = null;
      }
    })();

    return state.inflightFetch;
  }

  function ensureInjectedSlot() {
    if (state.injectedSlot && state.injectedSlot.isConnected)
      return state.injectedSlot;
    const slot = document.createElement("div");
    slot.id = "pack-buy-box-sampler-slot";
    slot.className = "shopify-block shopify-app-block";
    slot.setAttribute("data-pack-injected", "true");
    if (state.singlePlaceholder && state.singlePlaceholder.isConnected) {
      state.singlePlaceholder.insertAdjacentElement("beforebegin", slot);
    } else {
      singleMoonbundleWrap.insertAdjacentElement("afterend", slot);
    }
    state.injectedSlot = slot;
    return slot;
  }

  // Co-running A/B-test scripts (session-info, loyalty badge versions, trust
  // test) bake their Liquid values at server-render time and use one-shot
  // IIFEs that mount widgets onto the FIRST Moonbundle they find on the
  // page. After our swap they're stuck with single's data. We re-inject
  // the same scripts from the sampler PDP HTML so their IIFEs run again
  // with sampler-correct values and mount widgets onto the sampler slot.
  function injectSamplerTestScripts() {
    if (!state.samplerTestScripts || !state.samplerTestScripts.length) return;
    removeSamplerTestScripts();
    const container = document.createElement("div");
    container.id = "pack-buy-box-test-scripts";
    state.samplerTestScripts.forEach((text) => {
      const s = document.createElement("script");
      s.textContent = text;
      container.appendChild(s);
    });
    document.body.appendChild(container);
    state.testScriptsContainer = container;
  }

  function removeSamplerTestScripts() {
    if (state.testScriptsContainer && state.testScriptsContainer.isConnected) {
      state.testScriptsContainer.remove();
    }
    state.testScriptsContainer = null;
  }

  // Plan A: physically detach the single Moonbundle when entering sampler mode
  // so Moonbundle's listeners (subscription toggle, tier click, ATC binding,
  // earn-points calc) re-bind to the only remaining instance — the sampler.
  // A placeholder <template> keeps the position so we can re-attach on switch back.
  function detachSingleMoonbundle() {
    if (!singleMoonbundleWrap.isConnected) return;
    const placeholder = document.createElement("template");
    placeholder.id = "pack-single-moonbundle-placeholder";
    singleMoonbundleWrap.insertAdjacentElement("beforebegin", placeholder);
    state.singlePlaceholder = placeholder;
    state.singleMoonbundleNode = singleMoonbundleWrap;
    singleMoonbundleWrap.remove();
  }

  function attachSingleMoonbundle() {
    if (singleMoonbundleWrap.isConnected) return;
    if (state.singlePlaceholder && state.singlePlaceholder.isConnected) {
      state.singlePlaceholder.insertAdjacentElement(
        "beforebegin",
        singleMoonbundleWrap,
      );
      state.singlePlaceholder.remove();
      state.singlePlaceholder = null;
    }
  }

  function moveAriaCurrent(to) {
    if (singleCard) {
      if (to === "single") singleCard.setAttribute("aria-current", "page");
      else singleCard.removeAttribute("aria-current");
    }
    if (samplerCard) {
      if (to === "sampler") samplerCard.setAttribute("aria-current", "page");
      else samplerCard.removeAttribute("aria-current");
    }
  }

  // The page's loyalty badge (ab-version-a/b-loyalty.liquid) computes
  // "Earn N Reward Points" via MutationObservers tied to the original single
  // Moonbundle's price element. After detach, those observers are orphaned
  // and the badge stays stuck on the single-product value. Mirror its math
  // ourselves and rewrite the text whenever the sampler tier or subscription
  // option changes.
  function recalcLoyaltyBadge() {
    const slot = state.injectedSlot;
    if (!slot) return;
    const activeTier = slot.querySelector(
      ".moonbundle-option.moonbundle-option-active",
    );
    const priceEl =
      activeTier?.querySelector(
        ".moonbundle-option-single-price, .moonbundle-option-new-price",
      ) ||
      slot.querySelector(
        ".moonbundle-option-new-price, .moonbundle-option-single-price",
      );
    const text = (priceEl?.textContent || "").replace(/,/g, ".");
    const m = text.match(/\d+(?:\.\d+)?/);
    const price = m ? Math.floor(parseFloat(m[0])) : 0;
    if (!price) return;
    const points = price * 5;
    document
      .querySelectorAll(".ab-loyalty-badge__left span")
      .forEach((span) => {
        if (/reward points on this order/i.test(span.textContent)) {
          span.textContent = "Earn " + points + " Reward Points on this order";
        }
      });
  }

  function bindSamplerLoyaltyListeners() {
    const slot = state.injectedSlot;
    if (!slot || slot.dataset.loyaltyBound) return;
    slot.dataset.loyaltyBound = "true";
    slot.addEventListener("click", (e) => {
      if (
        e.target.closest(
          ".moonbundle-option, .moonbundle-subscription-radio-option",
        )
      ) {
        setTimeout(recalcLoyaltyBadge, 50);
        setTimeout(recalcLoyaltyBadge, 250);
      }
    });
    new MutationObserver(() => setTimeout(recalcLoyaltyBadge, 50)).observe(
      slot,
      {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      },
    );
  }

  function syncSamplerAvailability() {
    if (!state.samplerProduct) return;
    const available = !!state.samplerProduct.available;
    const atc = document.querySelector("button.add-to-cart");
    if (!atc) return;
    if (state.mode === "sampler" && !available) {
      atc.disabled = true;
      atc.setAttribute("data-sampler-soldout", "true");
      const labelEl = atc.querySelector("span") || atc;
      if (!atc.dataset.origLabel) atc.dataset.origLabel = labelEl.textContent;
      labelEl.textContent = "Sold Out";
    } else if (atc.dataset.samplerSoldout) {
      atc.disabled = false;
      atc.removeAttribute("data-sampler-soldout");
      const labelEl = atc.querySelector("span") || atc;
      if (atc.dataset.origLabel) {
        labelEl.textContent = atc.dataset.origLabel;
        delete atc.dataset.origLabel;
      }
    }
  }

  function normalizeSamplerVariantImages(slot) {
    if (
      !slot ||
      !root.classList.contains("ab-pack-v1") ||
      !samplerOverrideImageUrl
    )
      return;
    slot
      .querySelectorAll(
        '.variant-input-wrap[data-handle="select-flavor"] label',
      )
      .forEach((label) => {
        const original =
          label.querySelector(".variant-img-original") ||
          Array.from(label.querySelectorAll("img")).find((img) =>
            (img.currentSrc || img.src || "").includes("Brain_Fuel_5_Pack"),
          );
        if (!original) return;

        original.classList.add("variant-img-original");

        if (label.querySelector(".variant-img-override")) return;

        const override = document.createElement("img");
        override.className = "variant-img-override";
        override.src = samplerOverrideImageUrl;
        override.alt = original.alt || "";
        override.width = 440;
        override.height = 252;
        original.insertAdjacentElement("afterend", override);
      });
  }

  function syncSamplerVariantHeight(slot) {
    if (!slot || !root.classList.contains("ab-pack-v1")) return;

    const singleLabel = document.querySelector(
      '[data-dynamic-variants-enabled]:not(#pack-buy-box-sampler-slot [data-dynamic-variants-enabled]) .variant-input-wrap[data-handle="select-flavor"] .variant-input label,' +
        '[data-dynamic-variants-enabled]:not(#pack-buy-box-sampler-slot [data-dynamic-variants-enabled]) .variant-input-wrap[data-handle="flavor"] .variant-input label',
    );
    const height = singleLabel?.getBoundingClientRect().height || 0;
    if (height > 0) {
      slot.style.setProperty(
        "--pack-variant-card-height",
        Math.ceil(height) + "px",
      );
    }
  }

  function getSamplerCopyFromLabel(labelText) {
    const count = (labelText || "").match(/\d+/)?.[0];
    return {
      primary: count
        ? count + " Flavor Sampler"
        : samplerCard?.dataset.packCopyPrimary || "Sampler Pack",
      secondary: samplerCard?.dataset.packCopySecondary || "One of each flavor",
    };
  }

  function parseFlavorList(raw) {
    return String(raw || "")
      .split("|")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  // Icons from single-product flavor pills, else flavor-icon-{handle}.svg
  function flavorIconUrl(name) {
    const singleWrap = document.querySelector(
      "[data-dynamic-variants-enabled]:not(#pack-buy-box-sampler-slot [data-dynamic-variants-enabled]) " +
        '.variant-input-wrap[data-handle="select-flavor"], ' +
        "[data-dynamic-variants-enabled]:not(#pack-buy-box-sampler-slot [data-dynamic-variants-enabled]) " +
        '.variant-input-wrap[data-handle="flavor"]',
    );
    if (singleWrap) {
      const input = Array.from(
        singleWrap.querySelectorAll(".variant-input[data-value]"),
      ).find((el) => el.getAttribute("data-value") === name);
      const fromData = input?.getAttribute("data-flavor-icon");
      if (fromData) return fromData;
      const img = input?.querySelector(".variant-flavor-icon");
      const fromImg = img?.currentSrc || img?.src || "";
      if (fromImg) return fromImg;
    }
    const opt = Array.from(
      document.querySelectorAll(
        "[data-flavor-select] .variant-flavor-select__option[data-value]",
      ),
    ).find((el) => el.getAttribute("data-value") === name);
    if (opt?.getAttribute("data-icon")) return opt.getAttribute("data-icon");

    if (!scriptSrc || !name) return "";
    const handle = String(name)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    const fileHandle =
      handle === "pineapple"
        ? "tropical"
        : handle === "rebed-red-apple"
          ? "rebel-red-apple"
          : handle === "jelly-donut"
            ? "jelly-doughnut"
            : handle === "peach-rings"
              ? "peach-ring"
              : handle;
    const known = [
      "american-spice",
      "arctic-dew",
      "banana-runtz",
      "blue-razz",
      "cherry-lemonade",
      "fresh-wintergreen",
      "grape",
      "jelly-doughnut",
      "kiwi-strawberry",
      "mint",
      "mint-ice",
      "mocha",
      "natural-mint",
      "peach-ring",
      "rebel-red-apple",
      "southern-peach",
      "tangy-citrus",
      "toasted-coconut",
      "tropical",
      "tropical-mango",
      "watermelon",
      "wintergreen",
    ];
    if (!known.includes(fileHandle)) return "";
    try {
      return new URL("flavor-icon-" + fileHandle + ".svg", scriptSrc).href;
    } catch (_) {
      return "";
    }
  }

  function buildIncludesNodes(flavors) {
    const frag = document.createDocumentFragment();
    if (!flavors.length) {
      frag.appendChild(
        document.createTextNode(
          samplerCard?.dataset.packCopySecondary || "One of each flavor",
        ),
      );
      return frag;
    }
    flavors.forEach((name) => {
      const item = document.createElement("span");
      item.className = "pack-buy-box--sampler-includes-item";
      const iconUrl = flavorIconUrl(name);
      if (iconUrl) {
        const img = document.createElement("img");
        img.className = "pack-buy-box--sampler-includes-icon";
        img.src = iconUrl;
        img.alt = "";
        img.width = 16;
        img.height = 20;
        img.loading = "lazy";
        img.decoding = "async";
        item.appendChild(img);
      }
      item.appendChild(document.createTextNode("1x " + name));
      frag.appendChild(item);
    });
    return frag;
  }

  function syncSamplerFlavorHeading(slot, mode) {
    const heading = slot.querySelector(".variant-wrapper .variant__label");
    if (!heading) return;
    if (!heading.dataset.packOriginalLabel) {
      heading.dataset.packOriginalLabel = heading.textContent.trim();
    }
    if (mode === "includes") {
      heading.textContent = "THIS SAMPLER INCLUDES THE NEXT FLAVORS:";
      heading.classList.add("pack-buy-box--sampler-includes-heading");
      heading.classList.remove("pack-buy-box--sampler-select-heading");
      return;
    }
    if (mode === "select") {
      heading.textContent = "Select SAMPLER PACK Flavor:";
      heading.classList.add("pack-buy-box--sampler-select-heading");
      heading.classList.remove("pack-buy-box--sampler-includes-heading");
      return;
    }
    heading.textContent = heading.dataset.packOriginalLabel;
    heading.classList.remove(
      "pack-buy-box--sampler-includes-heading",
      "pack-buy-box--sampler-select-heading",
    );
  }

  // Metafield list → includes chip; empty + multi → variant title; empty + single → fallback copy
  function setSamplerOptionLabel(variantInput, { multi }) {
    const label = variantInput?.querySelector("label");
    if (!label) return;
    const flavors = parseFlavorList(
      variantInput.getAttribute("data-sampler-flavors"),
    );
    const title = variantInput.getAttribute("data-value") || "";
    const span = label.querySelector(":scope > span");

    if (flavors.length || !multi) {
      label.classList.add("pack-buy-box--sampler-includes");
      if (span) span.textContent = "";
      let list = label.querySelector(".pack-buy-box--sampler-includes-list");
      if (!list) {
        list = document.createElement("p");
        list.className =
          "pack-buy-box--v1__copy pack-buy-box--v1__copy--variant pack-buy-box--sampler-includes-list";
        label.appendChild(list);
      }
      list.replaceChildren(buildIncludesNodes(flavors));
      return;
    }

    label.classList.remove("pack-buy-box--sampler-includes");
    if (span && title) span.textContent = title;
    label.querySelector(".pack-buy-box--v1__copy--variant")?.remove();
  }

  function clearSamplerIncludes(slot) {
    slot.querySelectorAll(".variant-input label").forEach((label) => {
      label.classList.remove("pack-buy-box--sampler-includes");
      const title =
        label.closest(".variant-input")?.getAttribute("data-value") || "";
      const span = label.querySelector(":scope > span");
      if (span && title) span.textContent = title;
      label.querySelector(".pack-buy-box--v1__copy--variant")?.remove();
    });
    slot.classList.remove("pack-buy-box--multi-sampler");
  }

  function syncSamplerVariantCopy(slot) {
    if (!slot || !root.classList.contains("ab-pack-v1")) return;

    const options = Array.from(
      slot.querySelectorAll(".variant-input-wrap .variant-input"),
    );
    if (!options.length) return;
    const multi = options.length > 1;

    if (root.classList.contains("ab-flavor-v2")) {
      syncSamplerFlavorHeading(slot, "select");
      clearSamplerIncludes(slot);
      if (typeof window.syncAbFlavorSelectSamplerMode === "function") {
        window.syncAbFlavorSelectSamplerMode(slot);
      }
      return;
    }

    // Pill chips — live default (formerly ab-flavor-v1)
    syncSamplerFlavorHeading(slot, "includes");
    slot.classList.toggle("pack-buy-box--multi-sampler", multi);
    options.forEach((variantInput) =>
      setSamplerOptionLabel(variantInput, { multi }),
    );
  }

  function bindSamplerVariantCopy(slot) {
    if (!slot || slot.dataset.packCopyBound) return;
    slot.dataset.packCopyBound = "true";
    slot.addEventListener("change", (event) => {
      if (event.target.matches("[data-variant-input]")) {
        syncSamplerVariantCopy(slot);
      }
    });
  }

  function scheduleSamplerVariantCopy(slot) {
    syncSamplerVariantCopy(slot);
    setTimeout(() => syncSamplerVariantCopy(slot), 50);
    setTimeout(() => syncSamplerVariantCopy(slot), 250);
  }

  async function switchToSampler(triggerEl) {
    if (state.mode === "sampler") return;
    triggerEl?.setAttribute("aria-busy", "true");
    // Only show the loader when we actually need the network — subsequent
    // toggles read from cache and feel instant.
    const needsFetch = !state.samplerMarkup;
    if (needsFetch) root.setAttribute("data-pack-loading", "true");
    try {
      await loadSamplerData();
      detachSingleMoonbundle();
      const slot = ensureInjectedSlot();
      if (!slot.innerHTML) {
        slot.innerHTML =
          (state.samplerVariantPickerMarkup || "") + state.samplerMarkup;
      }
      normalizeSamplerVariantImages(slot);
      syncSamplerVariantHeight(slot);
      bindSamplerVariantCopy(slot);
      root.setAttribute(STATE_ATTR, "sampler");
      state.mode = "sampler";
      moveAriaCurrent("sampler");
      scheduleSamplerVariantCopy(slot);
      syncV1CardIcons();
      syncSamplerAvailability();
      if (
        root.classList.contains("ab-flavor-v2") &&
        typeof window.initAbFlavorSelects === "function"
      ) {
        window.initAbFlavorSelects(slot);
      }
      // Defer one tick so Moonbundle's web component lifecycle hydrates
      // first; the test scripts walk the DOM looking for .moonbundle-option.
      setTimeout(injectSamplerTestScripts, 50);
    } catch (err) {
      console.warn(
        "[pack-buy-box] swap failed, falling back to navigation:",
        err,
      );
      attachSingleMoonbundle();
      if (samplerUrl) window.location.href = samplerUrl;
    } finally {
      triggerEl?.removeAttribute("aria-busy");
      root.removeAttribute("data-pack-loading");
    }
  }

  function switchToSingle() {
    if (state.mode === "single") return;
    removeSamplerTestScripts();
    attachSingleMoonbundle();
    if (state.injectedSlot && state.injectedSlot.isConnected) {
      state.injectedSlot.remove();
      state.injectedSlot = null;
    }
    root.setAttribute(STATE_ATTR, "single");
    state.mode = "single";
    moveAriaCurrent("single");
    syncSamplerAvailability();
  }

  // ATC routing: Moonbundle wires the page's single "Add to cart" button to
  // the FIRST <moonbundle-component> (single product). When in sampler mode
  // we intercept and POST /cart/add.js with the sampler variant + the tier
  // multiplier picked in the injected sampler widget.
  function readSamplerTierMultiplier() {
    const slot = state.injectedSlot;
    if (!slot) return 1;
    const active = slot.querySelector(
      ".moonbundle-option-active, .moonbundle-option.moonbundle-option-active",
    );
    if (!active) return 1;
    const m = (active.textContent || "").match(/(\d+)/);
    return m ? parseInt(m[1], 10) / 5 : 1;
  }

  function readSamplerSellingPlanId() {
    const slot = state.injectedSlot;
    if (!slot) return null;
    const activeSub = slot.querySelector(
      ".moonbundle-subscription-radio-option.active",
    );
    if (!activeSub) return null;
    // Look for selling_plan id on the active subscription option
    const sp =
      activeSub.getAttribute("data-selling-plan-id") ||
      activeSub.dataset?.sellingPlanId ||
      activeSub
        .querySelector("[data-selling-plan-id]")
        ?.getAttribute("data-selling-plan-id");
    return sp || null;
  }

  async function handleSamplerATC(e) {
    if (state.mode !== "sampler") return;
    const variantId = resolveSamplerVariantId();
    if (!variantId) return;
    e.preventDefault();
    e.stopImmediatePropagation();

    if (state.samplerProduct && !state.samplerProduct.available) {
      console.warn("[pack-buy-box] sampler sold out — ATC blocked");
      return;
    }

    // Use the theme's native ATC loading state (.btn--loading) — its CSS
    // already paints the gradient overlay and fades the label out, matching
    // what users see on a normal single-product ATC click.
    const atcButton = document.querySelector(
      'button.add-to-cart, button[name="add"], [data-add-to-cart]',
    );
    if (atcButton) {
      atcButton.disabled = true;
      atcButton.classList.add("btn--loading");
    }

    const multiplier = readSamplerTierMultiplier();
    const sellingPlanId = readSamplerSellingPlanId();

    const fd = new FormData();
    fd.append("id", variantId);
    fd.append("quantity", String(multiplier));
    if (sellingPlanId) fd.append("selling_plan", sellingPlanId);

    try {
      const routesRoot = window.Shopify?.routes?.root || "/";
      const r = await fetch(routesRoot + "cart/add.js", {
        method: "POST",
        body: fd,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => null);
        const msg =
          body?.description || body?.message || `cart/add returned ${r.status}`;
        throw new Error(msg);
      }
      const responseData = await r.json();
      const cart = await fetch(routesRoot + "cart.js")
        .then((res) => res.json())
        .catch(() => null);
      const eventDetail = { form: fd, response: responseData, cart };

      // Match the events <product-form> fires + the ajaxProduct:added event
      // that main-product-high-variant uses to open the cart drawer. Default
      // behavior: no hard redirect — open the drawer (or whatever the theme
      // listens to). If the store is in cartType=page mode, a listener can
      // still navigate by reading the cart:navigate event.
      document.dispatchEvent(
        new CustomEvent("product-form:submit:success", {
          bubbles: true,
          detail: eventDetail,
        }),
      );
      document.dispatchEvent(
        new CustomEvent("product-form:cart:update", {
          bubbles: true,
          detail: eventDetail,
        }),
      );
      document.dispatchEvent(
        new CustomEvent("ajaxProduct:added", {
          bubbles: true,
          detail: eventDetail,
        }),
      );
      const navEvt = new CustomEvent("product-form:cart:navigate", {
        bubbles: true,
        cancelable: true,
        detail: eventDetail,
      });
      const shouldNavigate = document.dispatchEvent(navEvt);
      const isCartPageMode =
        window.theme &&
        window.theme.settings &&
        window.theme.settings.cartType === "page";
      if (shouldNavigate && isCartPageMode)
        window.location.href = routesRoot + "cart";
    } catch (err) {
      console.error("[pack-buy-box] sampler ATC failed:", err);
      // Don't fall back to navigation here — that would dump the user on a
      // sold-out PDP. Surface the error inline.
      if (atcButton) {
        const labelEl = atcButton.querySelector("span") || atcButton;
        labelEl.textContent = String(
          err.message || "Unable to add — try again",
        );
      }
    } finally {
      if (atcButton && !atcButton.dataset.samplerSoldout) {
        atcButton.disabled = false;
        atcButton.classList.remove("btn--loading");
      }
    }
  }

  // Plan A + B combo: detach single from DOM (Plan A) so Moonbundle's tier
  // and subscription listeners re-bind to the sampler instance natively;
  // intercept ATC in capture phase (Plan B) and POST cart/add.js with the
  // sampler variant ourselves — because Moonbundle keeps the form's
  // product-id pinned to the PDP's main product and won't auto-rewrite it.
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!form || !form.action || !form.action.includes("/cart/add")) return;
      handleSamplerATC(e);
    },
    true,
  );
  document.addEventListener(
    "click",
    (e) => {
      const btn = e.target.closest(
        'button.add-to-cart, button[name="add"], [data-add-to-cart]',
      );
      if (!btn) return;
      if (state.mode !== "sampler") return;
      handleSamplerATC(e);
    },
    true,
  );

  // Capture phase + stopImmediatePropagation so any SPA-style link interceptor
  // (theme bundles often programmatically navigate on anchor clicks) can't run
  // after us and force a page change.
  document.addEventListener(
    "click",
    (e) => {
      const trigger = e.target.closest("[data-pack-buy-box-cta]");
      if (!trigger) return;
      const role = trigger.getAttribute("data-pack-buy-box-cta");
      if (role !== "sampler" && role !== "single") return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (role === "sampler") {
        switchToSampler(trigger);
      } else {
        switchToSingle();
      }
    },
    true,
  );

  function syncV1State() {
    syncV1CardIcons();
    prefetchSamplerCardIcon();
  }

  root.setAttribute(STATE_ATTR, "single");
  syncV1State();
  new MutationObserver(syncV1State).observe(root, {
    attributes: true,
    attributeFilter: ["class"],
  });
})();
